import type { JSX } from 'react'
import { useMemo } from 'react'
import { QrTooLongError, encodeQr } from './qr'

/**
 * QR как SVG.
 *
 * SVG, а не canvas: он масштабируется без размытия, попадает в печать и в
 * скриншот таким же, каким виден на экране, и не требует ref'а с эффектом.
 *
 * Картинка сознательно НЕ несёт смысла в одиночку: рядом всегда стоит та же
 * строка текстом (см. `TotpPanel`), потому что QR недоступен ни скринридеру, ни
 * человеку, который настраивает 2FA на том же устройстве, где открыта панель.
 * Поэтому здесь `aria-hidden`, а не `aria-label` с секретом внутри: озвучивать
 * секрет второго фактора вслух — плохая идея.
 */
export function QrCode({
  value,
  /** Сторона картинки в пикселях. Модуль получается `size / (модулей + 8)`. */
  size = 208,
  className
}: {
  value: string
  size?: number
  className?: string
}): JSX.Element | null {
  const matrix = useMemo(() => {
    try {
      return encodeQr(value)
    } catch (error) {
      // Слишком длинная строка — не повод ронять весь экран настроек:
      // вызывающий покажет текстовый вариант.
      if (error instanceof QrTooLongError) return null
      throw error
    }
  }, [value])

  if (matrix === null) return null

  // «Тихая зона» — 4 модуля по стандарту: без неё сканеры не находят код.
  const quiet = 4
  const side = matrix.size + quiet * 2

  const path = matrix.modules
    .flatMap((row, r) => row.map((dark, c) => (dark ? `M${c + quiet} ${r + quiet}h1v1h-1z` : null)))
    .filter((piece): piece is string => piece !== null)
    .join('')

  return (
    <svg
      // Картинка дублируется текстом рядом — для скринридера она шум.
      aria-hidden
      viewBox={`0 0 ${side} ${side}`}
      width={size}
      height={size}
      className={className}
      shapeRendering="crispEdges"
    >
      {/* Белый фон обязателен: тёмная тема панели сделала бы код нечитаемым. */}
      <rect width={side} height={side} fill="#ffffff" />
      <path d={path} fill="#000000" />
    </svg>
  )
}
